import {useCallback, useState} from 'react';

import {getRenderedGridRows} from 'studies/typed/shared/procedure-runs/conditions/conditionGroupGridRows';
import type {ExperimentMethod} from 'studies/typed/shared/procedure-runs/design-state/ExperimentalInputs';
import type {ConditionGroup} from 'studies/typed/shared/procedure-runs/design-state/useExperimentDesignState';

import {getProcedureRunDesignFlowchartNodeId} from './ProcedureRunDesignFlowchartNodeIdUtils';

export interface UseProcedureRunDesignNodeRemovalEditsResult {
  /** The set of node IDs currently tracked as removed in this edit session. */
  removedNodeIds: ReadonlySet<string>;
  /** Add newly removed node IDs into the tracked draft removal state. Used on confirmation of node removal. */
  updateRemovedNodeIds: (nodeIds: ReadonlySet<string>) => void;
  /** Clear all tracked node removals, for example when canceling edits. */
  resetNodeRemovals: () => void;
}

/**
 * Removes stale entries from `removedNodeIds`, dropping node IDs that do not correspond to live nodes.
 *
 * For example, node IDs are dropped after save and refetch when the newly fetched data results in those nodes
 * no longer being displayed as live nodes (e.g. because they are archived).
 *
 * This intentionally uses `getRenderedGridRows` rather than `numReplicates`, because persisted
 * replicate numbers can be sparse.
 *
 * @param removedNodeIds Node IDs currently tracked as removed.
 * @param conditions Current condition/replicate data.
 * @param methods Current procedure methods used to render the flowchart grid.
 * @returns The cleaned-up removed-node set.
 */
export function pruneRemovedNodeIds(
  removedNodeIds: ReadonlySet<string>,
  conditions: ReadonlyArray<ConditionGroup>,
  methods: ReadonlyArray<ExperimentMethod>
): ReadonlySet<string> {
  if (removedNodeIds.size === 0) {
    return removedNodeIds;
  }

  // `liveNodeIds` is all node IDs that currently render as live, non-archived flowchart cells.
  const liveNodeIds = new Set<string>();
  getRenderedGridRows(conditions, methods, false).forEach(({conditionGroupIndex, replicateIndex, cells}) => {
    cells.forEach((cell) => {
      liveNodeIds.add(
        getProcedureRunDesignFlowchartNodeId({
          conditionIndex: conditionGroupIndex,
          replicateIndex,
          methodId: cell.methodId,
        })
      );
    });
  });

  const pruned = new Set<string>();
  for (const nodeId of removedNodeIds) {
    if (liveNodeIds.has(nodeId)) {
      pruned.add(nodeId);
    }
  }
  return pruned;
}

/**
 * React hook that owns temporary removed-node state across edit, save, and refetch operations.
 *
 * Edits are pruned as conditions change and cleared only on explicit reset, so a save-window edit-mode
 * exit does not temporarily revive removed nodes.
 *
 * @param conditions Current conditions.
 * @param methods Current methods.
 * @returns Helpers and state for tracking removed nodes (see {@link UseProcedureRunDesignNodeRemovalEditsResult}).
 */
export function useProcedureRunDesignNodeRemovalEdits(
  conditions: ReadonlyArray<ConditionGroup>,
  methods: ReadonlyArray<ExperimentMethod>
): UseProcedureRunDesignNodeRemovalEditsResult {
  const [removedNodeIds, setRemovedNodeIds] = useState<ReadonlySet<string>>(() => new Set());

  const prunedRemovedNodeIds = pruneRemovedNodeIds(removedNodeIds, conditions, methods);
  // Return the pruned set immediately so stale removals do not affect rendering, then sync raw state
  // after render. This avoids React render-phase updates while keeping downstream consumers current.
  const renderedRemovedNodeIds =
    prunedRemovedNodeIds.size === removedNodeIds.size ? removedNodeIds : prunedRemovedNodeIds;

  if (renderedRemovedNodeIds !== removedNodeIds) {
    setRemovedNodeIds(renderedRemovedNodeIds);
  }

  const updateRemovedNodeIds = useCallback((nodeIds: ReadonlySet<string>) => {
    setRemovedNodeIds((current) => {
      const next = new Set([...current, ...nodeIds]);
      return next.size === current.size ? current : next;
    });
  }, []);

  const resetNodeRemovals = useCallback(() => {
    setRemovedNodeIds(new Set());
  }, []);

  return {
    removedNodeIds: renderedRemovedNodeIds,
    updateRemovedNodeIds,
    resetNodeRemovals,
  };
}
